import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { formatDate, parseDate } from '../utils/dateHelper';
import companyService from '../services/company';

const initialState = {
    booking_max_seats: undefined,
    currency: undefined,
    lang: undefined,
    langs: [],
    modules: [],
    name: undefined,
    booking_approval: true,
    booking_interval: formatDate(parseDate('00:30', 'HH:mm'), 'HH:mm'),
    booking_toleration: formatDate(parseDate('00:30', 'HH:mm'), 'HH:mm'),
    periods: [],
    bookings: [],
    disclaimers: {},
    phone: undefined,
    pics: false,
    qrcode_bg: undefined,
    qrcode_border: undefined,
    runnings: [],
    street_address: undefined,
    theme: undefined,
    timezone: undefined,
    loading: false,
    error: null,
};

// Async thunk for fetching company data
export const fetchCompany = createAsyncThunk(
    'company/fetchCompany',
    async (_, { rejectWithValue }) => {
        try {
            const response = await companyService.getCompany(true);
            if (response.status === 200) {
                return response.data;
            }
            return rejectWithValue({
                status: response.status,
                message: response.data?.message || 'Failed to fetch company data',
            });
        } catch (error) {
            return rejectWithValue({
                status: error.response?.status || 500,
                message: error.response?.data?.message || error.message || 'Network error',
            });
        }
    },
);

export const companySlice = createSlice({
    name: 'company',
    initialState,
    reducers: {
        update: (state, action) => {
            state.booking_max_seats = action.payload.booking_max_seats;
            state.currency = action.payload.currency;
            state.lang = action.payload.lang;
            state.langs = action.payload.langs?.slice();
            state.modules = action.payload.modules?.slice();
            state.name = action.payload.name;
            state.disclaimers = action.payload.disclaimers;
            state.booking_approval = action.payload.booking_approval;
            state.booking_interval = formatDate(parseDate(action.payload.booking_interval, 'HH:mm'), 'HH:mm');
            state.booking_toleration = formatDate(parseDate(action.payload.booking_toleration, 'HH:mm'), 'HH:mm');
            state.periods = action.payload.periods?.slice();
            state.periods && state.periods.forEach(element => {
                if (element.time) {
                    element.time = {
                        start: formatDate(parseDate(element.time.start, 'HH:mm'), 'HH:mm'),
                        end: formatDate(parseDate(element.time.end, 'HH:mm'), 'HH:mm'),
                    };
                }
            });

            state.bookings = action.payload.bookings?.slice();
            state.bookings && state.bookings.forEach(element => {
                if (element.time) {
                    element.time = {
                        start: formatDate(parseDate(element.time.start, 'HH:mm'), 'HH:mm'),
                        end: formatDate(parseDate(element.time.end, 'HH:mm'), 'HH:mm'),
                    };
                }
            });

            state.phone = action.payload.phone;
            state.pics = action.payload.pics;
            state.qrcode_bg = action.payload.qrcode_bg;
            state.qrcode_border = action.payload.qrcode_border;

            state.runnings = action.payload.runnings?.slice();
            state.runnings && state.runnings.forEach(element => {
                if (element.time) {
                    element.time = {
                        start: formatDate(parseDate(element.time.start, 'HH:mm:ss'), 'HH:mm'),
                        end: formatDate(parseDate(element.time.end, 'HH:mm:ss'), 'HH:mm'),
                    };
                }
            });

            state.street_address = action.payload.street_address;
            state.theme = action.payload.theme;
            state.timezone = action.payload.timezone;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchCompany pending
            .addCase(fetchCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            // fetchCompany fulfilled
            .addCase(fetchCompany.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                
                // Update all company fields
                state.booking_max_seats = action.payload.booking_max_seats;
                state.currency = action.payload.currency;
                state.lang = action.payload.lang;
                state.langs = action.payload.langs?.slice();
                state.modules = action.payload.modules?.slice();
                state.name = action.payload.name;
                state.disclaimers = action.payload.disclaimers;
                state.booking_approval = action.payload.booking_approval;
                state.booking_interval = formatDate(parseDate(action.payload.booking_interval, 'HH:mm'), 'HH:mm');
                state.booking_toleration = formatDate(parseDate(action.payload.booking_toleration, 'HH:mm'), 'HH:mm');
                state.periods = action.payload.periods?.slice();
                state.periods && state.periods.forEach(element => {
                    if (element.time) {
                        element.time = {
                            start: formatDate(parseDate(element.time.start, 'HH:mm'), 'HH:mm'),
                            end: formatDate(parseDate(element.time.end, 'HH:mm'), 'HH:mm'),
                        };
                    }
                });

                state.bookings = action.payload.bookings?.slice();
                state.bookings && state.bookings.forEach(element => {
                    if (element.time) {
                        element.time = {
                            start: formatDate(parseDate(element.time.start, 'HH:mm'), 'HH:mm'),
                            end: formatDate(parseDate(element.time.end, 'HH:mm'), 'HH:mm'),
                        };
                    }
                });

                state.phone = action.payload.phone;
                state.pics = action.payload.pics;
                state.qrcode_bg = action.payload.qrcode_bg;
                state.qrcode_border = action.payload.qrcode_border;

                state.runnings = action.payload.runnings?.slice();
                state.runnings && state.runnings.forEach(element => {
                    if (element.time) {
                        element.time = {
                            start: formatDate(parseDate(element.time.start, 'HH:mm:ss'), 'HH:mm'),
                            end: formatDate(parseDate(element.time.end, 'HH:mm:ss'), 'HH:mm'),
                        };
                    }
                });

                state.street_address = action.payload.street_address;
                state.theme = action.payload.theme;
                state.timezone = action.payload.timezone;
            })
            // fetchCompany rejected
            .addCase(fetchCompany.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || {
                    status: 500,
                    message: 'Failed to fetch company data',
                };
            });
    },
});

// Action creators
export const { update, clearError } = companySlice.actions;

// Selectors
export const selectCompany = (state) => state.company;
export const selectCompanyName = (state) => state.company.name;
export const selectCompanyLangs = (state) => state.company.langs;
export const selectCompanyModules = (state) => state.company.modules;
export const selectCompanyTheme = (state) => state.company.theme;
export const selectCompanyTimezone = (state) => state.company.timezone;
export const selectCompanyPeriods = (state) => state.company.periods;
export const selectCompanyBookings = (state) => state.company.bookings;
export const selectCompanyDisclaimers = (state) => state.company.disclaimers;
export const selectCompanyLoading = (state) => state.company.loading;
export const selectCompanyError = (state) => state.company.error;

export default companySlice.reducer;
